// 16章 建具工事 — やさしい解説
// 原文は src/spec_index.json（PDFから自動生成）。このファイルは手書きで管理する。
// SVGは currentColor を使い、ライト/ダーク両テーマに追従させる。

import type { Commentary } from "../types";

const SVG_PERFORMANCE = `
<svg viewBox="0 0 660 250" role="img" aria-label="外部に面する建具の3つの性能。耐風圧性Sは風で変形・破損しない強さ、気密性Aは隙間風の少なさ、水密性Wは雨水の浸入しにくさ。等級が大きいほど高性能">
  <g fill="none" stroke="currentColor" stroke-width="2">
    <rect x="240" y="50" width="180" height="160"/>
    <line x1="330" y1="50" x2="330" y2="210"/>
  </g>
  <g stroke="currentColor" stroke-width="1.6">
    <line x1="120" y1="90" x2="235" y2="90"/>
    <line x1="425" y1="130" x2="500" y2="130"/>
    <line x1="425" y1="175" x2="500" y2="150"/>
  </g>
  <g fill="currentColor">
    <polygon points="235,90 223,84 223,96"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold">
    <text x="40" y="80">耐風圧性 S</text>
    <text x="510" y="115">気密性 A</text>
    <text x="510" y="160">水密性 W</text>
  </g>
  <g fill="currentColor" font-size="11.5" opacity="0.85">
    <text x="40" y="100">風で変形・破損しない</text>
    <text x="510" y="133">すき間風の少なさ</text>
    <text x="510" y="178">雨水の浸入しにくさ</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="40" y="235">JIS A 4702（ドアセット）・A 4706（サッシ）による。等級が大きいほど高性能（例 S-6＞S-4）（16.2.2）</text>
  </g>
</svg>`;

const SVG_MATERIAL_MAP = `
<svg viewBox="0 0 660 250" role="img" aria-label="建具の種類。アルミニウム製・樹脂製は主に窓サッシで軽く錆びない、樹脂は断熱性が高い。鋼製・鋼製軽量・ステンレス製は主にドア、鋼製は防火戸に、ステンレスは水回りに。木製は屋内の戸に使う">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="20" y="50" width="200" height="170" rx="10"/>
    <rect x="230" y="50" width="200" height="170" rx="10"/>
    <rect x="440" y="50" width="200" height="170" rx="10"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold" text-anchor="middle">
    <text x="120" y="76">窓（サッシ）系</text>
    <text x="330" y="76">ドア（鋼系）</text>
    <text x="540" y="76">その他</text>
  </g>
  <g fill="currentColor" font-size="12">
    <text x="36" y="104">・アルミニウム製（2節）</text>
    <text x="48" y="124" opacity="0.8">軽く錆びない・主力</text>
    <text x="36" y="148">・樹脂製（3節）</text>
    <text x="48" y="168" opacity="0.8">断熱性が高い（H等級）</text>
    <text x="246" y="104">・鋼製（4節）→防火戸</text>
    <text x="246" y="128">・鋼製軽量（5節）→屋内</text>
    <text x="246" y="152">・ステンレス製（6節）</text>
    <text x="258" y="172" opacity="0.8">→水回り・耐食</text>
    <text x="456" y="104">・木製（7節）屋内の戸</text>
    <text x="456" y="128">・シャッター（11-12節）</text>
    <text x="456" y="152">・オーバーヘッドドア</text>
    <text x="456" y="176">・自動ドア・ガラス</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="20" y="240">共通のゴール：所定の耐風圧性・気密性・水密性・耐震性能を持つこと（16.1.2）</text>
  </g>
</svg>`;

const SVG_INSTALL = `
<svg viewBox="0 0 660 250" role="img" aria-label="建具枠の取付け。コンクリート系下地はくさびで仮留め後サッシアンカーを鉄筋等に溶接固定し、くさびを撤去して枠と躯体の間にモルタルを密実に充填する。アンカーは両端から逃げた位置から間隔500ミリ以下">
  <g fill="currentColor" opacity="0.25"><rect x="60" y="40" width="70" height="180"/><rect x="430" y="40" width="70" height="180"/></g>
  <g fill="none" stroke="currentColor" stroke-width="3"><rect x="130" y="60" width="300" height="140"/></g>
  <g fill="currentColor" opacity="0.4"><rect x="120" y="50" width="20" height="160"/><rect x="420" y="50" width="20" height="160"/></g>
  <g stroke="currentColor" stroke-width="2">
    <line x1="120" y1="90" x2="90" y2="90"/><line x1="120" y1="140" x2="90" y2="140"/><line x1="120" y1="190" x2="90" y2="190"/>
    <line x1="440" y1="90" x2="470" y2="90"/><line x1="440" y1="140" x2="470" y2="140"/><line x1="440" y1="190" x2="470" y2="190"/>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="55" y="30" opacity="0.85">躯体</text>
    <text x="250" y="130" text-anchor="middle" font-weight="bold">建具枠</text>
    <text x="140" y="235">① 仮留め → ② アンカーを鉄筋等に溶接</text>
    <text x="140" y="252" opacity="0.85">③ モルタル密実充填（アンカー間隔500mm以下）</text>
  </g>
  <g fill="currentColor" font-size="11.5" opacity="0.75">
    <text x="80" y="42">充填モルタル</text>
  </g>
</svg>`;

const SVG_GLASS_SETTING = `
<svg viewBox="0 0 660 250" role="img" aria-label="ガラスのはめ込み。ガラス溝には面クリアランス、エッジクリアランス、掛り代を確保し、下部にセッティングブロックを敷いてガラスを溝の中央に保つ。網入り・複層ガラスの下端溝には径6ミリ以上の水抜き孔を2か所以上設ける">
  <g fill="none" stroke="currentColor" stroke-width="2">
    <path d="M100 60 L100 200 L160 200 L160 180 L120 180 L120 80 L160 80 L160 60 Z"/>
  </g>
  <g fill="currentColor" opacity="0.35"><rect x="128" y="70" width="10" height="120"/></g>
  <g fill="currentColor" opacity="0.7"><rect x="122" y="178" width="22" height="10"/></g>
  <g fill="currentColor"><circle cx="140" cy="195" r="3"/></g>
  <g stroke="currentColor" stroke-width="1.5">
    <line x1="120" y1="120" x2="128" y2="120"/>
    <line x1="138" y1="72" x2="138" y2="66"/>
  </g>
  <g fill="currentColor" font-size="12">
    <text x="180" y="90" font-weight="bold">ガラス溝の3寸法</text>
    <text x="180" y="114">a 面クリアランス（ガラスと溝側面）</text>
    <text x="180" y="138">b エッジクリアランス（端部の余裕）</text>
    <text x="180" y="162">c 掛り代（溝にのみ込む深さ）</text>
    <text x="180" y="192">セッティングブロック（下部で支える）</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="40" y="235">網入り・複層ガラスの下端溝には径6mm以上の水抜き孔を2か所以上（結露水・浸入水を排出）（16.14.3）</text>
  </g>
</svg>`;

const ch16: Record<string, Commentary> = {
  "16.1.1": {
    plainSummary:
      "この章はアルミ製・樹脂製・鋼製・鋼製軽量・ステンレス製・木製の各建具、建具用金物、自動ドア、上吊り引戸、重量/軽量シャッター、オーバーヘッドドア、ガラスの建具工事に適用する。1章とセットで適用する。",
    why: "建具は窓とドアの総称です。材料（アルミ・樹脂・鋼・ステンレス・木）ごとに節が分かれ、それぞれ得意分野があります。共通の関心事は「風・雨・すき間風を防ぐ性能」と「金物の取付け」です。",
    diagrams: [
      {
        svg: SVG_MATERIAL_MAP,
        caption: "建具の種類と使い分け（16章の全体像）",
      },
    ],
  },

  "16.1.2": {
    plainSummary:
      "ゴールは3つ。①所定の材料 ②所定の形状・寸法で見え掛り部が所要の仕上り ③耐風圧性・気密性・水密性等の所定の性能と所要の耐震性能を持つこと。",
    diagrams: [
      {
        svg: SVG_PERFORMANCE,
        caption: "外部建具の3性能：耐風圧性・気密性・水密性（16.1.2）",
      },
    ],
  },

  "16.1.3": {
    plainSummary:
      "防火戸の適用は特記。防火戸は大臣が定めた構造方法または大臣認定品。自動閉鎖機構をヒューズ・熱感知器・煙感知器と連動させる場合は特記（防煙シャッターは煙感知器連動）。通行部分の防火戸は閉鎖時に周囲の人の安全を確保できるものとする。",
    why: "防火戸は火災時に自動で閉まって延焼・煙の拡散を防ぐ命綱です。ただし人がいる時に閉まると挟まれ事故になるため、危害防止機構（人を感知したら止まる）が求められます。",
    glossary: [
      {
        term: "防火戸・防火設備",
        meaning:
          "火災の延焼を防ぐため、一定時間火炎を遮る性能を持つ戸。防火区画に用いる。",
      },
    ],
  },

  "16.1.4": {
    plainSummary:
      "建具見本の製作は特記。特殊な建具の仮組は特記（実施する場合は仮組方法・確認項目等を記載した施工計画書を作成）。",
  },

  "16.1.5": {
    plainSummary:
      "施工後、建具の機能が満たされるよう調整する。モルタル・シーリング材・塗料が見え掛り面に付着したら直ちに除去する。",
    points: [
      "建具は取付けて終わりではなく調整が必須（開閉・施錠の具合）",
    ],
  },

  "16.1.6": {
    plainSummary:
      "開閉操作が複雑な建具は操作方法を表示する。防犯建物部品の適用は特記。",
    glossary: [
      {
        term: "防犯建物部品（CPマーク）",
        meaning:
          "侵入に対して一定の抵抗性能を持つと認定された建具・金物。CPマークで表示される。",
      },
    ],
  },

  "16.2.1": {
    plainSummary:
      "この節は既製のアルミ押出形材等で製作するアルミニウム製建具に適用する。",
    why: "アルミサッシは軽く錆びず加工しやすいため、窓の主力材料です。押出形材（型から押し出して作る断面材）を組み合わせて作ります。",
  },

  "16.2.2": {
    plainSummary:
      "性能・構造はドアセットはJIS A 4702、サッシはJIS A 4706による。外部建具の耐風圧性(S)・気密性(A)・水密性(W)の等級と枠見込み寸法は特記（コンクリート系/鉄骨下地は表16.2.1、木下地は表16.2.2）。防音・断熱ドアの等級は特記。",
    why: "サッシ選定の核心が3つの性能等級です。台風の多い地域・高層階は耐風圧性(S)を高く、寒冷地は断熱性を、雨の多い面は水密性(W)を重視します。複層ガラスを使う引違い窓は枠見込み100mmが必要、という寸法の連動も覚えましょう。",
    points: [
      "S（耐風圧）・A（気密）・W（水密）の等級で選ぶ",
      "複層ガラス使用時は枠見込みが大きくなる",
    ],
    glossary: [
      {
        term: "見込み寸法",
        meaning: "建具枠の奥行き（壁厚方向）の寸法。ガラスの厚みや性能で決まる。",
      },
    ],
  },

  "16.2.3": {
    plainSummary:
      "アルミ材は押出形材（JIS H 4100）・板材（JIS H 4000）。補強材・アンカーは鋼またはアルミ合金（鋼製は接触腐食防止）。アルミに接する小ねじはステンレス製。網戸・防虫網・防鳥網の材質も規定。枠回りモルタルは表15.3.3、シーリングは9章7節。",
    why: "アルミと鋼を直接触れさせると電食（接触腐食）でアルミが錆びるため、鋼製部材にはめっきを、接するねじにはステンレスを使います。この電食対策はアルミ建具の重要ポイントです。",
  },

  "16.2.4": {
    plainSummary:
      "枠・かまちのアルミ板厚は1.5mm以上。引違い・片引き障子はグレイジングチャンネルが使える構造、外部の引違い・片引き窓は網戸が付けられる構造。表面処理は14.2.1（陽極酸化）による。ステンレス製くつずりは厚1.5mm・仕上げHL。",
    glossary: [
      {
        term: "かまち",
        meaning: "障子や戸の四周を構成する枠材。",
      },
      {
        term: "くつずり",
        meaning: "ドア下枠の床面に接する部分。ステンレスで摩耗・腐食に耐える。",
      },
    ],
  },

  "16.2.5": {
    plainSummary:
      "枠・くつずりのアンカーは両端から逃げた位置から間隔500mm以下。雨水浸入のおそれのある接合部は止水処理。取付けはコンクリート系下地の場合、くさびで仮留め→サッシアンカーを鉄筋等に溶接またはねじ留め→くさび撤去→枠と躯体の間にモルタルを密実に充填。鉄骨下地は溶接・小ねじ留め（溶接部はAz種錆止め）。木下地はステンレス木ねじ・釘で留付け。",
    why: "サッシ取付けの核心は「仮留め→アンカー固定→モルタル充填」の流れ。モルタルを密実に充填しないと、風でサッシが動いたり、隙間から雨漏りしたりします。アンカー間隔500mm以下は、風圧に耐えるための基準です。",
    points: [
      "くさび仮留め→アンカー溶接→モルタル密実充填の順",
      "アンカー間隔は500mm以下",
      "雨水浸入のおそれのある接合部は止水処理",
    ],
    diagrams: [
      {
        svg: SVG_INSTALL,
        caption: "建具枠の取付け（16.2.5）",
      },
    ],
  },

  "16.3.1": {
    plainSummary:
      "この節は無可塑ポリ塩化ビニル製建具用形材等で製作する樹脂製建具に適用する。",
    why: "樹脂サッシはアルミより熱を伝えにくく、結露しにくい・断熱性が高いのが特徴。寒冷地や省エネ建築で採用が増えています。",
  },

  "16.3.2": {
    plainSummary:
      "性能・構造はJIS A 4702/4706による。耐風圧性・気密性・水密性の等級・枠見込みは特記（表16.3.1/16.3.2）。断熱ドア・サッシの断熱性等級は特記（表16.3.3、H-4〜H-8で熱貫流率が小さいほど高断熱）。",
    glossary: [
      {
        term: "熱貫流率（U値）",
        meaning:
          "建具の熱の伝わりやすさ（W/(㎡·K)）。小さいほど断熱性が高い。H-8は1.1以下。",
      },
    ],
  },

  "16.3.3": {
    plainSummary:
      "樹脂形材はJIS A 5558。補強材・アンカーは鋼・ステンレス・アルミ合金（鋼製は接触腐食防止）。ガラスは特記がなければ複層ガラス。枠回りモルタル・シーリングは規定による。",
    why: "樹脂は強度が金属より低いので、内部に鋼などの補強材を入れて剛性を確保します。断熱性を活かすため複層ガラスが標準です。",
  },

  "16.3.4": {
    plainSummary:
      "枠・かまちの主要部形材の樹脂肉厚は2.0mm以上。ガラスは押縁＋グレイジングガスケットが使える構造。外部の引違い・片引き窓は網戸が付けられる構造。外部建具の枠・かまちは水漏れ・すきま風防止のため溶着接合とする。",
    why: "樹脂サッシの角は「溶着（熱で溶かして一体化）」でつなぎます。ネジ留めだと隙間から水・風が入るため、外部建具は溶着が必須です。",
  },

  "16.3.5": {
    plainSummary:
      "樹脂製建具の製作・ガラスや押縁のはめ込みは原則製造所で行う。それ以外はアルミ建具に準ずる。取付けは16.2.5による。",
  },

  "16.4.1": {
    plainSummary: "この節は建築物に使用する鋼製建具・標準型鋼製建具に適用する。",
    why: "鋼製建具（スチールドア）は丈夫で防火性能を持たせやすいため、防火戸・玄関ドア・機械室扉などに使われます。",
  },

  "16.4.2": {
    plainSummary:
      "性能・構造はJIS A 4702/4706による。簡易気密型ドアセットの気密性・水密性等級は表16.4.1、外部鋼製建具の耐風圧性は表16.2.1により特記。耐震ドアの面内変形追随性等級は特記。",
    glossary: [
      {
        term: "面内変形追随性",
        meaning:
          "地震で壁が変形しても、ドアが変形に追随して開閉できる性能。耐震ドアに求められる。",
      },
    ],
  },

  "16.4.3": {
    plainSummary:
      "鋼板は溶融亜鉛めっき鋼板（付着量Z12/F12）でクロメートフリー化成皮膜処理品。くつずりはステンレス鋼板。上吊り引戸の下枠もステンレス。気密材は合成ゴム・合成樹脂。枠回りモルタル・シーリングは規定による。",
  },

  "16.4.4": {
    plainSummary:
      "鋼板類の厚さは特記がなければ表16.4.2（枠・戸のかまち・表面板1.6mm、くつずり1.5mm、力骨2.3mm等）。ただし1枚の戸の有効開口幅950mmまたは有効高さ2,400mm超は除く。塗装は18章、くつずり仕上げは16.2.4による。",
    why: "大きな戸ほど厚い鋼板が必要（たわみ・変形を防ぐ）。標準寸法を超える大型戸は個別に板厚を決めます。",
  },

  "16.4.5": {
    plainSummary:
      "組立は表16.4.3/16.4.4を標準とし、雨仕舞・開閉具合に注意。組立後は溶接部・隅角を平滑に仕上げ、雨水浸入のおそれのある接合部は止水処理。溶融亜鉛めっき鋼板の溶接部・損傷部は塗装前にパテ処理。枠の隅は溶接（屋内水掛り以外は小ねじ留め可）。取付けは16.2.5による。",
    points: [
      "外部に面する枠の隅・くつずりは溶接が原則",
      "屋内（水掛り除く）は小ねじ留め可（裏板2.3mm以上）",
    ],
  },

  "16.4.6": {
    plainSummary:
      "標準型鋼製建具は有効内法寸法（片開き幅800〜900等）・建具用金物を標準化したもの。錠はシリンダー箱錠（レバーハンドル）、ドアクローザは露出型。",
    why: "よく使う寸法・金物を標準化することで、コストを下げ品質を安定させます。設計・積算の効率化にもつながります。",
  },

  "16.5.1": {
    plainSummary:
      "この節は屋内に用いる軽量の鋼製建具・標準型鋼製軽量建具に適用する。",
    why: "鋼製軽量建具（LSD）は事務所・住宅の屋内ドアなど、防火や重厚さが不要な場所に使う軽量版。鋼製建具より薄い鋼板で作ります。",
  },

  "16.5.2": {
    plainSummary:
      "性能・構造はJIS A 4702/4706による。簡易気密型ドアセットの気密性等級はA-3、耐震ドアの面内変形追随性等級は特記。",
  },

  "16.5.3": {
    plainSummary:
      "鋼板は溶融/電気亜鉛めっき鋼板（付着量Z06/F06/E24等）、ビニル被覆鋼板、カラー鋼板。くつずり・上吊り引戸下枠はステンレス。戸の心材はペーパーコア・無機シートコア・発泡材（防音用はグラスウール/ロックウール）。",
    glossary: [
      {
        term: "ペーパーコア",
        meaning:
          "紙をハニカム（蜂の巣）状に成形した戸の芯材。軽くて反りにくい。",
      },
    ],
  },

  "16.5.4": {
    plainSummary:
      "鋼板類の厚さは特記がなければ表16.5.1（枠一般1.6mm、戸の表面板0.6mm以上、力骨・中骨1.6mm等）。戸の見込み寸法は35mm以上。塗装は18章、くつずり仕上げは16.2.4による。",
    why: "鋼製軽量建具は表面板0.6mm以上と鋼製建具（1.6mm）より薄い。屋内の軽い用途向けだからです。",
  },

  "16.5.5": {
    plainSummary:
      "組立は表16.5.2を標準とし開閉具合に注意。枠の隅は溶接（水掛り除き小ねじ留め可）。戸の表面板は心材にかぶせて接着圧着または力骨にかぶせ溶接。錠・丁番・ドアクローザ等の取付く箇所は裏面に補強板。取付けは16.2.5による。",
    points: [
      "金物取付け位置（錠・丁番・ドアクローザ）は裏面補強板が必須",
    ],
  },

  "16.6.1": {
    plainSummary: "この節は建築物に使用するステンレス製建具に適用する。",
    why: "ステンレス製建具は錆びに強く美観が持続するため、水回り・厨房・海沿い・高級な玄関などに使われます。",
  },

  "16.6.2": {
    plainSummary:
      "性能・構造はJIS A 4702/4706による。簡易気密型ドアセットの気密性・水密性等級は表16.4.1、外部の耐風圧性は表16.2.1により特記。耐震ドアの面内変形追随性等級は特記。",
  },

  "16.6.3": {
    plainSummary:
      "ステンレス鋼板はJIS G 4305（特記がなければSUS304等）。裏板・補強板は溶融亜鉛めっき鋼板。気密材・小ねじ・枠回りモルタル・シーリングは規定による。",
  },

  "16.6.4": {
    plainSummary:
      "鋼板類の厚さは表16.6.1（一般部・くつずり1.5mm、裏板1.6mm以上、補強板2.3mm以上）。表面仕上げは特記がなければHL。異種金属の接触腐食のおそれのある箇所は接触腐食防止対策。",
    glossary: [
      {
        term: "HL（ヘアライン）",
        meaning:
          "ステンレス表面に一方向の細かい筋目をつけた仕上げ。傷が目立ちにくく標準的。",
      },
    ],
  },

  "16.6.5": {
    plainSummary:
      "ステンレス鋼板の曲げ加工は普通曲げまたは角出し曲げ（特記がなければ普通曲げ）。角出し曲げで切込み後板厚0.75mm以下は裏板補強。各部材の組立は水掛り除き面内胴付き部を小ねじ・ボルト留め可。",
  },

  "16.7.1": {
    plainSummary:
      "この節は屋内に使用する木製建具に適用する。この節以外の仕様は製造所の仕様による。",
  },

  "16.7.2": {
    plainSummary:
      "建具材の加工・組立時の含水率は特記がなければA種（15%以下）。フラッシュ戸の表面材は合板・MDF（F☆☆☆☆等）。かまちは集成材・単板積層材。ガラス押縁のねじ・釘はステンレス製。",
    why: "木製建具も含水率15%以下が原則。乾いていない木で作ると、後で反って開閉しなくなります。",
    glossary: [
      {
        term: "フラッシュ戸",
        meaning:
          "かまちと心材の骨組みの両面に表面材（合板等）を張った平らな戸。屋内ドアの主流。",
      },
    ],
  },

  "16.7.3": {
    plainSummary:
      "フラッシュ戸の見込み寸法は表16.7.5（戸の大きさで30〜40mm）、表面板の厚さは特記がなければ表16.7.6（普通合板2.5mm以上等）。各木製建具（かまち戸・ふすま・障子）の見込みは表16.7.7。塗装は18章。",
  },

  "16.7.4": {
    plainSummary:
      "フラッシュ戸の工法は表16.7.8（かまちの構成、心材の中骨式/ペーパーコア式、表面板の接着圧着等）。かまち戸の工法は表16.7.9（ほぞの形式は見込み・見付け寸法で決まる）。",
    glossary: [
      {
        term: "かまち戸",
        meaning:
          "縦横のかまちで枠を組み、中に鏡板やガラスをはめた戸。伝統的な木製戸。",
      },
      {
        term: "ほぞ",
        meaning: "木材の接合で、一方に作った突起を相手の穴（ほぞ穴）に差し込む仕口。",
      },
    ],
  },

  "16.8.1": {
    plainSummary: "この節は建具に使用する建具用金物に適用する。",
  },

  "16.8.2": {
    plainSummary:
      "金物は建具に適した良質なもの。種類・見え掛り部の材質は特記がなければ表16.8.1（開き戸はシリンダー箱錠等）。亜鉛合金製・黄銅製は塗装・クロムめっき。湿気の多い箇所はステンレス等。主要な金物は見本品で承諾を受ける。",
    why: "金物（錠・丁番・ドアクローザ・ハンドル）は毎日触れ、故障すると使えなくなる重要部品。水回りはステンレス等の耐食金物を使うなど、場所に応じた材質選定が求められます。",
    glossary: [
      {
        term: "シリンダー箱錠",
        meaning: "鍵で施解錠するシリンダーと、ラッチ・デッドボルトを納めた箱状の錠。",
      },
      {
        term: "ドアクローザ",
        meaning: "開いた戸を自動的にゆっくり閉める金物。",
      },
    ],
  },

  "16.8.3": {
    plainSummary:
      "握り玉・レバーハンドル・押板・クレセントの取付け位置は特記。木製建具の金物は枠・建具の狂いを修正後に本取付け。金属・樹脂製建具の金物はねじ山が金属板に3山以上掛かるようにする。フロアヒンジを水掛りに設ける場合はやや高めに。",
  },

  "16.8.4": {
    plainSummary:
      "マスターキーの製作は特記。鍵は引渡し前に錠と照合して報告。製作本数は特記がなければ3本1組とし、室名札を付けて鍵箱に収納して引き渡す。",
    glossary: [
      {
        term: "マスターキー",
        meaning:
          "複数の異なる錠を1本で解錠できる親鍵。ビル管理で使う。",
      },
    ],
  },

  "16.9.1": {
    plainSummary:
      "この節は駆動装置と検出装置で構成される自動ドア開閉装置に適用する。",
  },

  "16.9.2": {
    plainSummary:
      "安全性はJIS A 4722による。戸の開閉方式は特記。駆動装置の性能は特記がなければ表16.9.1（引き戸用は片引き/引分け、適用戸質量で種類分け、耐久性50万サイクル以上等）。車椅子使用者用便房出入口の駆動装置性能は特記がなければ表16.9.2。",
    why: "自動ドアは人がぶつかる・挟まれる事故が起きやすいため、安全性のJIS規格が最優先。センサーの検出領域や閉じ力の上限が細かく決められています。",
  },

  "16.9.3": {
    plainSummary:
      "駆動装置は戸の質量に耐えるよう堅固に取り付ける。タッチスイッチは床から中心まで950mm程度。施工・調整後の性能は表16.9.5を満たす（閉じ力・開閉速度・反転停止距離・センサー検出領域等）。",
    points: [
      "センサーの検出領域・閉じ力・反転停止の調整が安全の要",
    ],
  },

  "16.10.1": {
    plainSummary:
      "この節は手動開き式の自閉式上吊り引戸装置に適用する。",
    glossary: [
      {
        term: "自閉式上吊り引戸",
        meaning:
          "上部レールで吊り、手で開けると自動的に閉まる引戸。床にレールがなくバリアフリーに有利。",
      },
    ],
  },

  "16.10.2": {
    plainSummary:
      "屋外用の主要構成材料はステンレス鋼板・アルミ材等の防錆性能を持つもの。それ以外は製造所の仕様による。",
  },

  "16.10.3": {
    plainSummary:
      "試験方法はJIS A 1518/1519/5545による。性能は特記がなければ表16.10.1（適用戸質量40kg以下/超で手動開き力15/20N以下等、閉り際で減速、20万回耐久試験）。",
  },

  "16.10.4": {
    plainSummary:
      "引戸装置は戸の質量に耐えるよう建具枠等に堅固に取り付ける。それ以外は製造所の仕様による。",
  },

  "16.11.1": {
    plainSummary:
      "この節は内のり幅8.0m以下・高さ4.0m以下の重量シャッターに適用する。規定外はJIS A 4705による。",
    glossary: [
      {
        term: "重量シャッター",
        meaning:
          "厚いスラットの丈夫なシャッター。防火・防煙・大開口の管理用に使う。",
      },
    ],
  },

  "16.11.2": {
    plainSummary:
      "シャッターの種類は管理用・外壁用防火・屋内用防火・防煙シャッターから特記。開閉方式は特記がなければ電動式（手動併用）。電動式は急降下制動・障害物感知装置を設ける。防火/防煙シャッターは危害防止機構（人を感知したら停止・反転）を設ける。",
    why: "重量シャッターは重く、落下・挟まれで死亡事故が起きています。だから急降下停止装置と危害防止機構（人を感知したら止まる・反転する）が安全の必須要素です。",
    points: [
      "電動式は急降下制動・障害物感知が必須",
      "防火/防煙シャッターは危害防止機構が必須",
    ],
  },

  "16.11.3": {
    plainSummary:
      "スラット・シャッターケースは溶融/塗装溶融亜鉛めっき鋼板（付着量Z12/F12）。ガイドレール・まぐさ・外部の座板等はステンレス鋼板。スイッチボックスのふたは錠付き。",
  },

  "16.11.4": {
    plainSummary:
      "鋼板類の厚さは表16.11.2（スラット・シャッターケース1.6mm、ガイドレール・まぐさ1.5mm、アンカー2.3mm）。スラットとガイドレールのかみ合わせ長さは表16.11.3（内法3m以下で左右合計90mm以上等）。塗装は18章。",
    why: "スラットとガイドレールのかみ合わせが浅いと、風圧でスラットがレールから外れます。開口が大きいほど深いかみ合わせが必要です。",
  },

  "16.11.5": {
    plainSummary:
      "組立は表16.11.4を標準（スラットは端金物でずれ止め、ガイドレールのアンカーは埋込み形600mm以下・露出形500mm以下）。取付けは16.2.5に準ずる。",
  },

  "16.12.1": {
    plainSummary:
      "この節は軽量シャッターに適用する。規定外はJIS A 4704・製造所の仕様による。",
    glossary: [
      {
        term: "軽量シャッター",
        meaning:
          "薄いスラットのシャッター。店舗の出入口や小さな開口に使う。スプリングで開閉。",
      },
    ],
  },

  "16.12.2": {
    plainSummary:
      "開閉方式は特記がなければ手動式（施錠装置付き）。耐風圧強度は特記。電動式は障害物感知装置による。",
  },

  "16.12.3": {
    plainSummary:
      "スラットは塗装溶融亜鉛めっき鋼板（Z06/F06）または塗装溶融55%アルミ亜鉛合金めっき鋼板（AZ90）で、材質は特記による。",
  },

  "16.12.4": {
    plainSummary:
      "鋼板の厚さは表16.12.2（スラット0.5mm、シャッターケース0.4mm、ガイドレール1.0mm等）。スラットはインターロッキング形/オーバーラッピング形から特記。ガイドレール溝深さ40mm以上、かみ合わせは他端20mm以上。",
  },

  "16.12.5": {
    plainSummary:
      "加工・組立は重量シャッターに準ずる。取付けは16.2.5に準ずる。",
  },

  "16.13.1": {
    plainSummary:
      "この節はオーバーヘッドドアに適用する。規定外はJIS A 4715・製造所の仕様による。",
    glossary: [
      {
        term: "オーバーヘッドドア",
        meaning:
          "パネル（セクション）を連結し、上方の天井面に沿って収納する大型の扉。ガレージ・倉庫・車庫に使う。",
      },
    ],
  },

  "16.13.2": {
    plainSummary:
      "セクション材料はスチール/アルミ/ファイバーグラスタイプ（特記がなければスチール）。風圧力による強さ区分は特記。開閉方式はバランス式/チェーン式/電動式（特記がなければバランス式）。収納形式はスタンダード形等から特記。電動式は障害物感知装置。",
  },

  "16.13.3": {
    plainSummary:
      "セクション材料はJIS A 4715。ガイドレールは溶融亜鉛めっき鋼板（Z27）またはステンレス鋼板（特記がなければ溶融亜鉛めっき鋼板）。",
  },

  "16.13.4": {
    plainSummary:
      "部材の厚さは表16.13.1（セクションは鋼板0.5mm・アルミ0.6mm・ファイバーグラス1.0mm、ガイドレール2.0mm）。",
  },

  "16.13.5": {
    plainSummary:
      "加工・組立・取付けは表16.13.2を標準（セクションはセンターヒンジ・ローラーヒンジで連結、ガイドレールは開口高より上は間隔2m以下・下は600mm以下で取付け）。",
  },

  "16.14.1": {
    plainSummary:
      "この節は建具に取り付けるガラス及びガラスブロックに適用する。",
  },

  "16.14.2": {
    plainSummary:
      "板ガラスはフロート板ガラス・型板ガラス・網入/線入板ガラス・合わせガラス・強化ガラス・熱線吸収ガラス・複層ガラス・熱線反射ガラス・倍強度ガラス（各JIS、種類は特記）。ガラス留め材はシーリング材・ガスケット。",
    why: "ガラスは用途で使い分けます。安全のための強化・合わせガラス、防火の網入りガラス、断熱の複層ガラス、日射制御の熱線吸収・反射ガラスなど。どこに何を使うかは特記で指定されます。",
    glossary: [
      {
        term: "複層ガラス",
        meaning:
          "2枚のガラスの間に空気層・ガス層を設けて断熱性を高めたガラス（ペアガラス）。",
      },
      {
        term: "合わせガラス",
        meaning:
          "中間膜でガラスを貼り合わせ、割れても飛散しにくくしたガラス。防犯・安全用。",
      },
    ],
  },

  "16.14.3": {
    plainSummary:
      "ガラス溝の大きさ（面クリアランスa・エッジクリアランスb・掛り代c）は特記。外部の網入/線入/合わせ/複層ガラスの下端溝には径6mm以上の水抜き孔を2か所以上（セッティングブロックでせき止めがある場合は中間に1か所追加）。",
    why: "ガラス溝には「クリアランス（隙間）」が必要。ガラスは温度で伸縮し、地震で建具が変形するため、ぴったり入れると割れます。水抜き孔は、溝に溜まった結露水や浸入水を逃がして、網入りガラスの錆割れ（サビによる膨張でガラスが割れる現象）を防ぎます。",
    points: [
      "ガラス溝には面・エッジのクリアランスと掛り代が必要",
      "下端溝に水抜き孔（径6mm以上・2か所以上）",
    ],
    glossary: [
      {
        term: "セッティングブロック",
        meaning:
          "ガラス溝の下部に敷いてガラスの重量を支える弾力性のブロック。",
      },
    ],
    diagrams: [
      {
        svg: SVG_GLASS_SETTING,
        caption: "ガラス溝の寸法とはめ込み（16.14.3）",
      },
    ],
  },

  "16.14.4": {
    plainSummary:
      "板ガラスの切断はクリアカットで正確に。外部の網入/線入板ガラスの下辺小口等は防錆処置。はめ込みはシーリング材（セッティングブロックを敷きガラスを溝中央に保つ）・グレイジングガスケット・押縁留め等。はめ込み後は破損防止の養生、完成直前に内外面を清掃。",
    points: [
      "網入りガラスの小口は防錆処置（錆割れ防止）",
      "はめ込み後は破損防止の養生を確実に",
    ],
  },

  "16.14.5": {
    plainSummary:
      "ガラスブロックはJIS A 5212に準じる。壁用金属枠・力骨（ステンレスSUS304）・緩衝材・滑り材・水抜きプレート等を用いる。壁用金属枠のアンカーは間隔450mm以下。目地幅は平積み8〜15mm。曲面積みは曲率半径をブロック幅の10倍以上。",
    glossary: [
      {
        term: "ガラスブロック",
        meaning:
          "中空の箱型ガラスを積み上げて壁を作る材料。採光しながら視線を遮り、意匠性が高い。",
      },
    ],
  },
};

export default ch16;
